import { Modal, Pressable, Text, TextInput, View } from "react-native";
import ColorPicker, { HueSlider, Panel1 } from "reanimated-color-picker";
import { useState } from "react";
import { ColorSwatch, Sun } from "iconsax-react-nativejs";
import ScButton from "../../ui-kit/button";
import { useTheme } from "@/src/styles/theme-provider";
import { useTranslation } from "react-i18next";

type ColorPickerModalProps = Readonly<{
  visible: boolean;
  initialColor: string;
  onClose: () => void;
  onApply: (hex: string) => void;
}>;

const ColorPickerModal = ({
  visible,
  initialColor,
  onClose,
  onApply,
}: ColorPickerModalProps) => {
  const { t } = useTranslation();

  const { themeVars, varStyle } = useTheme();

  const [pendingHex, setPendingHex] = useState(initialColor);
  const [hexInput, setHexInput] = useState(initialColor);

  const onSelectColor = ({ hex }: { hex: string }) => {
    "worklet";
    setPendingHex(hex);
    setHexInput(hex);
  };

  const handleHexTextInput = () => {
    if (/^[0-9A-Fa-f]{6}$/.test(hexInput)) {
      // Check valid hex color
      setPendingHex(hexInput);
    } else {
      setHexInput(pendingHex); // Otherwise reset to previous color
    }
  };

  const handleApply = () => {
    onApply(pendingHex);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <Pressable
        style={varStyle}
        className="flex-1 bg-shadow-dark items-center justify-center px-6"
        onPress={onClose}
      >
        <Pressable
          className="max-w-lg bg-ash rounded-3xl overflow-hidden shadow-2xl"
          onPress={(e) => e.stopPropagation()}
        >
          <ColorPicker
            style={{ width: "100%", padding: 16 }}
            value={pendingHex}
            onComplete={onSelectColor}
          >
            <Panel1
              thumbSize={24}
              boundedThumb={true}
              style={{ height: 220, borderRadius: 16 }}
            />

            <View className="pt-4 gap-3">
              <View className="flex-row items-center gap-3">
                <View className="w-7 h-7 items-center justify-center">
                  <ColorSwatch color={themeVars["--color-onyx-default"]} />
                </View>
                <View className="flex-1 h-6 justify-center">
                  <HueSlider
                    thumbSize={24}
                    boundedThumb={true}
                    style={{ height: 14, borderRadius: 16 }}
                  />
                </View>
              </View>

              <View className="flex-row items-center gap-2 mt-1 mb-2">
                <View className="px-4 py-2.5 rounded-xl border border-ash-3">
                  <Text className="text-sm font-medium text-onyx">
                    {t("LABEL_HEX")}
                  </Text>
                </View>

                <View className="flex-1 flex-row items-center px-4 py-2.5 rounded-xl bg-obsidian border border-ash-3">
                  <TextInput
                    value={hexInput.toUpperCase()}
                    onChangeText={(value) => setHexInput(value)}
                    onBlur={handleHexTextInput}
                    onSubmitEditing={handleHexTextInput}
                    maxLength={6}
                    autoCapitalize="characters"
                    autoCorrect={false}
                    className="text-sm font-medium text-onyx focus:outline-none justify-center p-0"
                  />
                </View>
              </View>
            </View>
          </ColorPicker>

          <View className="flex-row bg-ash justify-end border-t-[1px] border-t-ash-3 gap-3 px-4 py-4">
            <ScButton mode="outlined" text="BTN_CANCEL" onPress={onClose} />
            <ScButton text="BTN_APPLY" onPress={handleApply} />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default ColorPickerModal;
